// @flow

import {debounceTime, map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {ActionsObservable, ofType, StateObservable} from 'redux-observable';
import {arrayToOrderedMap, orderedMapToArray} from '../../utils/array';
import type {Action} from '../../store/utils';
import type {RootState} from '../index';

import {
  endpoints,
  getCustomersApi,
  getCustomerCategoriesApi,
  getCustomerApi,
  createCustomerCategoriesApi,
  saveCustomerMessageApi,
  getCustomerMessageApi,
  deleteCustomerCategoryApi,
  editCustomerApi,
  editCustomerCategoryApi,
} from './api';

import {buildRoute} from '../../http';

import type {
  User,
  Pagination,
  CrmCustomer,
  CustomerCategory,
  Order,
} from '../../entities';

export type CrmAction = Action & {
  error?: string,
  user?: User,
  queryFilter?: {[key: string]: string},
  categoryRequest?: {[key: string]: string},
  customerRequest?: {[key: string]: string},
  customerId?: string,
  categoryId?: string,
};

export type CrmState = {
  isSaving?: boolean,
  isFetchingCustomers?: boolean,
  isFetchingCustomer?: boolean,
  isFetchingCategories?: boolean,
  customers?: CrmCustomer[],
  categories?: CustomerCategory[],
  customer?: CrmCustomer,
  editCustomerSuccess?: boolean,
  customerError?: boolean,
  isSavingCategory?: boolean,
  createCategorySuccess?: boolean,
  categoryError?: string,
  fetchCustomersError?: boolean,
  fetchCustomerError?: boolean,
  fetchCategoriesError?: boolean,
  isFetchingMessage?: boolean,
  fetchMessageSuccess?: boolean,
  fetchMessageError?: string,
  isSavingMessage?: boolean,
  saveMessageSuccess?: boolean,
  editCategorySuccess?: boolean,
  deleteCategorySuccess?: boolean,
  saveMessageError?: string,
  message?: string,
  orders?: Order[],
  isFetchingOrders?: boolean,
  fetchCustomerOrdersError?: boolean,
  pagination?: Pagination,
  orderPagination?: Pagination,
};

export const ORDER_SIZE = 50;

// export class FetchReferral extends ActionCreator<ReferralAction> {}

export class FetchCustomers extends ActionCreator<CrmAction> {}
export class FetchCustomersSuccess extends ActionCreator<CrmAction> {}
export class FetchCustomersError extends ActionCreator<CrmAction> {}

export class FetchCustomer extends ActionCreator<CrmAction> {}
export class FetchCustomerSuccess extends ActionCreator<CrmAction> {}
export class FetchCustomerError extends ActionCreator<CrmAction> {}

export class EditCustomer extends ActionCreator<CrmAction> {}
export class EditCustomerSuccess extends ActionCreator<CrmAction> {}
export class EditCustomerError extends ActionCreator<CrmAction> {}

export class FetchCustomerOrders extends ActionCreator<CrmAction> {}
export class FetchCustomerOrdersSuccess extends ActionCreator<CrmAction> {}
export class FetchCustomerOrdersError extends ActionCreator<CrmAction> {}

export class FetchCustomerMessage extends ActionCreator<CrmAction> {}
export class FetchCustomerMessageSuccess extends ActionCreator<CrmAction> {}
export class FetchCustomerMessageError extends ActionCreator<CrmAction> {}

export class SaveCustomerMessage extends ActionCreator<CrmAction> {}
export class SaveCustomerMessageSuccess extends ActionCreator<CrmAction> {}
export class SaveCustomerMessageError extends ActionCreator<CrmAction> {}

export class FetchCustomerCategories extends ActionCreator<CrmAction> {}
export class FetchCustomerCategoriesSuccess extends ActionCreator<CrmAction> {}
export class FetchCustomerCategoriesError extends ActionCreator<CrmAction> {}

export class CreateCustomerCategory extends ActionCreator<CrmAction> {}
export class CreateCustomerCategorySuccess extends ActionCreator<CrmAction> {}
export class CreateCustomerCategoryError extends ActionCreator<CrmAction> {}

export class EditCustomerCategory extends ActionCreator<CrmAction> {}
export class EditCustomerCategorySuccess extends ActionCreator<CrmAction> {}
export class EditCustomerCategoryError extends ActionCreator<CrmAction> {}

export class DeleteCustomerCategory extends ActionCreator<CrmAction> {}
export class DeleteCustomerCategorySuccess extends ActionCreator<CrmAction> {}
export class DeleteCustomerCategoryError extends ActionCreator<CrmAction> {}

export class ResetCustomer extends ActionCreator<CrmAction> {}
export class ResetPartialState extends ActionCreator<CrmAction> {}

export const actions = {
  resetCustomer: () => new ResetCustomer(),
  resetPartialState: () => new ResetPartialState(),
  fetchCustomers: (payload: CrmAction) => new FetchCustomers(payload),
  fetchCustomer: (payload: CrmAction) => new FetchCustomer(payload),
  editCustomer: (payload: CrmAction) => new EditCustomer(payload),
  fetchCustomerOrders: (payload: CrmAction) => new FetchCustomerOrders(payload),
  fetchCustomerMessage: () => new FetchCustomerMessage(),
  saveCustomerMessage: (payload: CrmAction) => new SaveCustomerMessage(payload),
  fetchCustomerCategories: (payload: CrmAction) =>
    new FetchCustomerCategories(payload),
  createCustomerCategory: (payload: CrmAction) =>
    new CreateCustomerCategory(payload),
  editCustomerCategory: (payload: CrmAction) =>
    new EditCustomerCategory(payload),
  deleteCustomerCategory: (payload: CrmAction) =>
    new DeleteCustomerCategory(payload),
};

export const initialState = {};
export default createReducer(initialState, {
  [FetchCustomers.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isFetchingCustomers: true,
    queryFilter: action.queryFilter,
    // customers: [],
  }),
  [FetchCustomersSuccess.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isFetchingCustomers: false,
    customers: action.customers,
    pagination: action.pagination,
    fetchCustomersError: undefined,
  }),
  [FetchCustomersError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isFetchingCustomers: false,
    fetchCustomersError: action.error,
  }),
  [FetchCustomer.type]: (state: CrmState) => ({
    ...state,
    isFetchingCustomer: true,
  }),
  [FetchCustomerSuccess.type]: (state: CrmState, action: CrmAction) => {
    return {
      ...state,
      isFetchingOrders: false,
      isFetchingCustomer: false,
      customer: action.customer,
      orders: action.customer.orders ? action.customer.orders.data : [],
      orderPagination: action.orderPagination,
      fetchCustomerError: undefined,
    };
  },
  [FetchCustomerError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isFetchingCustomers: false,
    fetchCustomerError: action.error,
  }),
  [FetchCustomerOrders.type]: (state: CrmState) => ({
    ...state,
    isFetchingOrders: true,
    fetchCustomerOrdersError: undefined,
  }),
  [FetchCustomerOrdersSuccess.type]: (state: CrmState, action: CrmAction) => {
    const oldOrders = arrayToOrderedMap(state.orders, 'id');
    const newOrders = arrayToOrderedMap(action.data, 'id');
    const orders = orderedMapToArray(oldOrders.merge(newOrders));
    return {
      ...state,
      orders,
      isFetchingOrders: false,
      orderPagination: action.orderPagination,
      fetchCustomerOrdersError: undefined,
    };
  },
  [FetchCustomerOrdersError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isFetchingOrders: false,
    fetchCustomerOrdersError: action.error,
  }),
  [FetchCustomerCategories.type]: (state: CrmState) => ({
    ...state,
    isFetchingCategories: true,
  }),
  [FetchCustomerCategoriesSuccess.type]: (
    state: CrmState,
    action: CrmAction
  ) => ({
    ...state,
    isFetchingCategories: false,
    categories: action.categories,
    fetchCategoriesError: undefined,
  }),
  [FetchCustomerCategoriesError.type]: (
    state: CrmState,
    action: CrmAction
  ) => ({
    ...state,
    isFetchingCategories: false,
    fetchCategoriesError: action.error,
  }),
  [CreateCustomerCategory.type]: (state: CrmState) => ({
    ...state,
    isSavingCategory: true,
    createCategorySuccess: undefined,
    categoryError: undefined,
  }),
  [CreateCustomerCategorySuccess.type]: (state: CrmState) => ({
    ...state,
    isSavingCategory: false,
    createCategorySuccess: true,
    categoryError: undefined,
  }),
  [CreateCustomerCategoryError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isSavingCategory: false,
    createCategorySuccess: undefined,
    categoryError: action.error,
  }),
  [EditCustomerCategory.type]: (state: CrmState) => ({
    ...state,
    isSavingCategory: true,
    editCategorySuccess: undefined,
    categoryError: undefined,
  }),
  [EditCustomerCategorySuccess.type]: (state: CrmState) => ({
    ...state,
    isSavingCategory: false,
    editCategorySuccess: true,
    categoryError: undefined,
  }),
  [EditCustomerCategoryError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isSavingCategory: false,
    editCategorySuccess: undefined,
    categoryError: action.error,
  }),
  [DeleteCustomerCategory.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isSavingCategory: true,
    deleteCategorySuccess: undefined,
    categoryError: undefined,
  }),
  [DeleteCustomerCategorySuccess.type]: (
    state: CrmState,
    action: CrmAction
  ) => ({
    ...state,
    isSavingCategory: true,
    deleteCategorySuccess: true,
    categoryError: undefined,
  }),
  [DeleteCustomerCategoryError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isSavingCategory: true,
    deleteCategorySuccess: undefined,
    categoryError: action.error,
  }),
  [EditCustomer.type]: (state: CrmState) => ({
    ...state,
    isSaving: true,
    editCustomerSuccess: undefined,
    customerError: undefined,
  }),
  [EditCustomerSuccess.type]: (state: CrmState) => ({
    ...state,
    isSaving: false,
    editCustomerSuccess: true,
    customerError: undefined,
  }),
  [EditCustomerError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isSaving: false,
    editCustomerSuccess: undefined,
    customerError: action.error,
  }),
  [FetchCustomerMessage.type]: (state: CrmState) => ({
    ...state,
    isFetchingMessage: true,
    fetchMessageSuccess: undefined,
    fetchMessageError: undefined,
  }),
  [FetchCustomerMessageSuccess.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isFetchingMessage: false,
    fetchMessageSuccess: true,
    fetchMessageError: undefined,
    message: action.message,
  }),
  [FetchCustomerMessageError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isFetchingMessage: false,
    fetchMessageSuccess: undefined,
    fetchMessageError: action.error,
    message: '',
  }),
  [SaveCustomerMessage.type]: (state: CrmState) => ({
    ...state,
    isSavingMessage: true,
    saveMessageSuccess: undefined,
    saveMessageError: undefined,
  }),
  [SaveCustomerMessageSuccess.type]: (state: CrmState) => ({
    ...state,
    isSavingMessage: false,
    saveMessageSuccess: true,
    saveMessageError: undefined,
  }),
  [SaveCustomerMessageError.type]: (state: CrmState, action: CrmAction) => ({
    ...state,
    isSavingMessage: false,
    saveMessageSuccess: undefined,
    saveMessageError: action.error,
  }),
  [ResetCustomer.type]: (state: CrmState) => ({
    ...state,
    customer: undefined,
    orders: undefined,
    orderPagination: undefined,
  }),
  [ResetPartialState.type]: (state: CrmState) => ({
    ...state,
    editCustomerSuccess: undefined,
    createCategorySuccess: undefined,
    deleteCategorySuccess: undefined,
    editCategorySuccess: undefined,
    saveMessageSuccess: undefined,
  }),
});

export function fetchCustomersEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(
      FetchCustomers.type,
      EditCustomerSuccess.type,
      DeleteCustomerCategorySuccess.type
    ),
    debounceTime(250),
    switchMap(({queryFilter}: CrmAction) => {
      let query = undefined;
      if (queryFilter) query = queryFilter;
      else if (state$.value.crm.queryFilter)
        query = {...state$.value.crm.queryFilter};
      return getCustomersApi(query).pipe(
        map(
          ({data, pagination}: {data: CrmCustomer[], pagination: Pagination}) =>
            new FetchCustomersSuccess({customers: data, pagination})
        ),
        buildCatchError(FetchCustomersError, 'Error al obtener los clientes')
      );
    })
  );
}

export function fetchCustomerEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchCustomer.type),
    debounceTime(250),
    switchMap(({customerId}: CrmAction) => {
      const orderPagination = state$.value.crm.orderPagination;
      const qf = !orderPagination
        ? {page: 0, size: ORDER_SIZE}
        : {page: orderPagination.page + 1, size: ORDER_SIZE};
      // $FlowFixMe
      return getCustomerApi(qf, {
        route: buildRoute(endpoints.FETCH_CUSTOMER, [customerId]),
      }).pipe(
        map((customer: CrmCustomer) => {
          const orderPagination = customer.orders
            ? customer.orders.pagination
            : {};
          return new FetchCustomerSuccess({customer, orderPagination});
        }),
        buildCatchError(FetchCustomerError, 'Error al obtener el cliente')
      );
    })
  );
}

export function fetchCustomerOrdersEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchCustomerOrders.type),
    debounceTime(250),
    switchMap(({customerId}: CrmAction) => {
      const orderPagination = state$.value.crm.orderPagination;
      const qf = !orderPagination
        ? {page: 0, size: ORDER_SIZE}
        : {page: orderPagination.page + 1, size: ORDER_SIZE};
      // $FlowFixMe
      return getCustomerApi(qf, {
        route: buildRoute(endpoints.FETCH_CUSTOMER, [customerId]),
      }).pipe(
        map((customer: CrmCustomer) => {
          const data = customer.orders ? customer.orders.data : [];
          const orderPagination = customer.orders
            ? customer.orders.pagination
            : [];
          return new FetchCustomerOrdersSuccess({data, orderPagination});
        }),
        buildCatchError(
          FetchCustomerOrdersError,
          'Error al obtener ordenes del cliente'
        )
      );
    })
  );
}

export function editCustomerEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(EditCustomer.type),
    debounceTime(250),
    switchMap(({customerRequest, customerId}: CrmAction) => {
      return editCustomerApi(customerRequest, {
        route: buildRoute(endpoints.EDIT_CUSTOMER, [customerId]),
      }).pipe(
        map(() => new EditCustomerSuccess()),
        buildCatchError(EditCustomerError, 'Error al editar la categoría.')
      );
    })
  );
}

export function fetchCustomerCategoriesEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(
      FetchCustomerCategories.type,
      CreateCustomerCategorySuccess.type,
      DeleteCustomerCategorySuccess.type,
      EditCustomerCategorySuccess.type
    ),
    switchMap(() => {
      return getCustomerCategoriesApi().pipe(
        map(data => new FetchCustomerCategoriesSuccess({categories: data})),
        buildCatchError(
          FetchCustomerCategoriesError,
          'Error al obtener las categorias de clientes.'
        )
      );
    })
  );
}

export function createCustomerCategoryEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(CreateCustomerCategory.type),
    switchMap(({categoryRequest}: CrmAction) => {
      return createCustomerCategoriesApi(categoryRequest).pipe(
        map(() => new CreateCustomerCategorySuccess()),
        buildCatchError(
          CreateCustomerCategoryError,
          'Error al crear la categoría.'
        )
      );
    })
  );
}
export function editCustomerCategoryEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(EditCustomerCategory.type),
    switchMap(({categoryRequest, categoryId}: CrmAction) => {
      return editCustomerCategoryApi(categoryRequest, {
        route: buildRoute(endpoints.EDIT_CUSTOMER_CATEGORY, [categoryId]),
      }).pipe(
        map(() => new EditCustomerCategorySuccess()),
        buildCatchError(
          EditCustomerCategoryError,
          'Error al editar la categoría.'
        )
      );
    })
  );
}

export function deleteCustomerCategoryEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(DeleteCustomerCategory.type),
    switchMap(({categoryRequest}: CrmAction) => {
      return deleteCustomerCategoryApi({
        route: buildRoute(endpoints.EDIT_CUSTOMER_CATEGORY, [
          categoryRequest.id,
        ]),
      }).pipe(
        map(() => new DeleteCustomerCategorySuccess()),
        buildCatchError(
          DeleteCustomerCategoryError,
          'Error al eliminar la categoría.'
        )
      );
    })
  );
}

export function getCustomerMessageEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchCustomerMessage.type, SaveCustomerMessageSuccess.type),
    switchMap(({message}: CrmAction) => {
      return getCustomerMessageApi({message}).pipe(
        map(({message}) => new FetchCustomerMessageSuccess({message})),
        buildCatchError(FetchCustomerMessageError, 'Error')
      );
    })
  );
}

export function saveCustomerMessageEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(SaveCustomerMessage.type),
    switchMap(({message}: CrmAction) => {
      return saveCustomerMessageApi({message}).pipe(
        map(() => new SaveCustomerMessageSuccess()),
        buildCatchError(
          SaveCustomerMessageError,
          'Error al guardar el mensaje.'
        )
      );
    })
  );
}
